import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import { permissionGroupsApi, PermissionGroupData, SystemFunction, ActionOption } from '../api/permissionGroupsApi';
import { accountsApi, Operator } from '../api/accountsApi';
import { groupFunctions, getGroupedPermission, updateGroupedPermission, GroupedFunction } from '../utils/functionGrouping';

// Helper to check if code has CRUD suffix
function hasCrudSuffix(code: string): boolean {
  const crudSuffixes = ['_LIST', '_VIEW', '_ADD', '_EDIT', '_DELETE', '_REMOVE', '_IMPORT'];
  return crudSuffixes.some(suffix => code.endsWith(suffix));
}

export const PermissionGroupAdd: React.FC = () => {
  const { t } = useLang();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<PermissionGroupData>({
    name: '',
    description: '',
    operatorId: '',
    permissions: [],
  });
  const [availableFunctions, setAvailableFunctions] = useState<SystemFunction[]>([]);
  const [groupedFunctions, setGroupedFunctions] = useState<GroupedFunction[]>([]);
  const [availableActions, setAvailableActions] = useState<ActionOption[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      const [functions, actions, operatorsData] = await Promise.all([
        permissionGroupsApi.getAvailableFunctions(),
        permissionGroupsApi.getAvailableActions(),
        isSuperAdmin ? accountsApi.getOperators() : Promise.resolve([])
      ]);
      setAvailableFunctions(functions);
      
      // Group functions by base name
      const grouped = groupFunctions(functions);
      setGroupedFunctions(grouped);
      
      // Map action values to translated labels
      const translatedActions = actions.map(action => ({
        value: action.value,
        label: t(action.value) || action.label // Use translation key if available, fallback to original label
      }));
      setAvailableActions(translatedActions);
      setOperators(operatorsData);
      
      // Initialize permissions with empty array (will be populated when checkboxes are checked)
      setFormData(prev => ({
        ...prev,
        permissions: [],
      }));
    } catch (error) {
      console.error('Error loading initial data:', error);
      alert(t('errorLoadingFunctions'));
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field: keyof PermissionGroupData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePermissionChange = (groupedFunction: GroupedFunction, action: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: updateGroupedPermission(prev.permissions, groupedFunction, action, checked),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert(t('pleaseEnterGroupName'));
      return;
    }

    // Validation for SUPERADMIN
    if (isSuperAdmin && !formData.operatorId) {
      alert(t('pleaseSelectOperator'));
      return;
    }

    // Filter out permissions with no actions
    const permissionsWithActions = formData.permissions.filter(perm => perm.actions.length > 0);
    
    const submitData: PermissionGroupData = {
      ...formData,
      permissions: permissionsWithActions,
    };

    // Clean up data based on role
    if (!isSuperAdmin) {
      delete submitData.operatorId; // Backend will assign to current operator
    }

    try {
      setLoading(true);
      await permissionGroupsApi.create(submitData);
      alert(t('permissionGroupCreatedSuccess'));
      navigate('/permission-groups');
    } catch (error: any) {
      alert(error.response?.data?.message || t('errorCreatingPermissionGroup'));
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">{t('loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>{t('accountManagement')}</span> / 
          <span className="text-cyan-600 mx-1">{t('permissionGroups')}</span> / 
          <span className="text-gray-900">{t('addPermissionGroup')}</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('addPermissionGroup')}</h1>
        <p className="text-gray-600">{t('createNewPermissionGroup')}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Basic Info */}
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('generalInfo')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('groupName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterGroupName')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('description')}
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterGroupDescription')}
                  />
                </div>

                {/* Operator Selection - Only for SUPERADMIN */}
                {isSuperAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('operator')} *
                    </label>
                    <select
                      value={formData.operatorId || ''}
                      onChange={(e) => handleInputChange('operatorId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      required
                    >
                      <option value="">{t('selectOperator')}</option>
                      {operators.map((operator) => (
                        <option key={operator.id} value={operator.id}>
                          {operator.name} ({operator.email})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('selectOperatorForPermissionGroup')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Permissions */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('functionPermissions')}</h2>
              
              {availableFunctions.length === 0 ? (
                <div className="text-center py-8">
                  <div className="mb-4">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Không có chức năng hệ thống</h3>
                    <p className="text-gray-500 mb-2">Để tạo nhóm phân quyền, bạn cần tạo các chức năng hệ thống trước.</p>
                    <p className="text-sm text-gray-400">Vui lòng truy cập vào menu "Chức năng" và nhấn nút "Tạo chức năng mặc định".</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/functions')}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Đi tới Chức năng để tạo mặc định
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Actions Header */}
                  <div
                    className="grid gap-2 pb-2 border-b border-gray-200"
                    style={{ gridTemplateColumns: `minmax(0, 2fr) repeat(${availableActions.length}, minmax(0, 1fr))` }}
                  >
                    <div className="font-medium text-sm text-gray-700">{t('function')}</div>
                    {availableActions.map(action => (
                      <div key={action.value} className="text-center font-medium text-sm text-gray-700">
                        {action.label}
                      </div>
                    ))}
                  </div>

                  {/* Function Permissions - Grouped */}
                  {groupedFunctions.map(group => {
                    return (
                      <div
                        key={group.baseCode}
                        className="grid gap-2 py-3 border-b border-gray-100"
                        style={{ gridTemplateColumns: `minmax(0, 2fr) repeat(${availableActions.length}, minmax(0, 1fr))` }}
                      >
                        <div className="flex items-center">
                          <div>
                            <div className="font-medium text-sm text-gray-900">{group.displayName}</div>
                            <div className="text-xs text-gray-500">{group.baseCode}</div>
                          </div>
                        </div>
                        {availableActions.map(action => {
                          const hasPermission = getGroupedPermission(formData.permissions, group, action.value);
                          // Only show checkbox if the action has a corresponding function
                          const hasFunction = !!group.actionToFunction[action.value];
                          return (
                            <div key={action.value} className="flex justify-center">
                              <input
                                type="checkbox"
                                checked={hasPermission}
                                disabled={!hasFunction}
                                onChange={(e) => handlePermissionChange(group, action.value, e.target.checked)}
                                className="text-cyan-600 focus:ring-cyan-500 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title={!hasFunction ? `${action.label} không khả dụng cho ${group.displayName}` : ''}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/permission-groups')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all duration-200"
          >
            {loading ? t('creating') : t('save')}
          </button>
        </div>
      </form>
    </div>
  );
}; 